import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Play,
  BookOpen,
  ArrowSquareOut,
  CaretDown,
  CaretRight,
  MonitorPlay,
  Clock,
  X
} from '@phosphor-icons/react';
import { STAGE_RESOURCES } from './stageResources';

// YouTube Player Modal
const VideoPlayer = ({ videoId, title, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
    onClick={onClose}
    data-testid="video-player-modal"
  >
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0 }}
      className="relative w-full max-w-4xl"
      onClick={e => e.stopPropagation()}
    >
      <button
        onClick={onClose}
        className="absolute -top-10 right-0 text-zinc-400 hover:text-white transition-colors"
        data-testid="video-player-close"
      >
        <X size={24} />
      </button>
      <div className="relative pb-[56.25%] rounded-lg overflow-hidden bg-zinc-900">
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
        />
      </div>
      <p className="text-white font-outfit font-semibold mt-3 text-center">{title}</p>
    </motion.div>
  </motion.div>
);

// Video Card
const VideoCard = ({ video, onPlay }) => (
  <button
    onClick={() => onPlay(video)}
    className="group relative bg-zinc-900/60 border border-white/10 rounded-lg overflow-hidden hover:border-orange-500/30 transition-all text-left w-full"
    data-testid={`video-card-${video.id}`}
  >
    {/* Thumbnail */}
    <div className="relative aspect-video bg-zinc-800">
      <img
        src={`https://img.youtube.com/vi/${video.id}/mqdefault.jpg`}
        alt={video.title}
        className="w-full h-full object-cover"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors flex items-center justify-center">
        <div className="w-12 h-12 rounded-full bg-orange-500/90 group-hover:bg-orange-500 flex items-center justify-center transition-all group-hover:scale-110">
          <Play size={22} weight="fill" className="text-white ml-0.5" />
        </div>
      </div>
      <span className="absolute bottom-2 right-2 text-xs bg-black/70 text-white px-2 py-0.5 rounded font-mono">
        {video.duration}
      </span>
    </div>
    {/* Info */}
    <div className="p-3">
      <h4 className="font-medium text-white text-sm leading-snug group-hover:text-orange-400 transition-colors line-clamp-2">
        {video.title}
      </h4>
      <p className="text-xs text-zinc-500 mt-1">{video.creator}</p>
      <p className="text-xs text-zinc-400 mt-2 line-clamp-2">{video.description}</p>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {video.tags.map((tag, i) => (
          <span key={i} className="text-[10px] px-2 py-0.5 bg-orange-500/10 text-orange-400/80 rounded">
            {tag}
          </span>
        ))}
      </div>
    </div>
  </button>
);

// Reading Card
const ReadingCard = ({ reading }) => (
  <a
    href={reading.url}
    target="_blank"
    rel="noopener noreferrer"
    className="group flex gap-4 p-4 bg-zinc-900/60 border border-white/10 rounded-lg hover:border-orange-500/30 transition-all"
    data-testid={`reading-${reading.title.replace(/\s+/g, '-').toLowerCase().slice(0, 30)}`}
  >
    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center">
      <span className="text-[10px] font-bold text-zinc-400 uppercase">{reading.type}</span>
    </div>
    <div className="flex-1 min-w-0">
      <h4 className="font-medium text-white text-sm group-hover:text-orange-400 transition-colors">
        {reading.title}
      </h4>
      <p className="text-xs text-zinc-500 mt-0.5">{reading.source}</p>
      <p className="text-xs text-zinc-400 mt-1.5 line-clamp-2">{reading.description}</p>
    </div>
    <ArrowSquareOut size={18} className="text-zinc-600 group-hover:text-orange-400 flex-shrink-0 mt-1 transition-colors" />
  </a>
);

// Stage Resources Section
const StageSection = ({ stageId, resources, isExpanded, onToggle, onPlayVideo }) => {
  const stageColors = {
    1: '#00C2A8', 2: '#3B82F6', 3: '#8B5CF6', 4: '#F59E0B',
    5: '#10B981', 6: '#EF4444', 7: '#06B6D4', 8: '#F97316'
  };
  const color = stageColors[stageId] || '#F59E0B';

  return (
    <div className="border border-white/10 rounded-lg overflow-hidden" data-testid={`resources-stage-${stageId}`}>
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-center gap-4 hover:bg-zinc-800/30 transition-colors"
      >
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center text-lg font-outfit font-bold"
          style={{ background: `${color}20`, color }}
        >
          {stageId}
        </div>
        <div className="flex-1 text-left">
          <span className="text-xs text-zinc-500 font-medium">STAGE {stageId}</span>
          <h3 className="font-outfit font-semibold text-white text-sm">{resources.stageName}</h3>
        </div>
        <div className="flex items-center gap-3 text-xs text-zinc-500">
          <span className="flex items-center gap-1">
            <MonitorPlay size={14} />
            {resources.videos.length} videos
          </span>
          <span className="flex items-center gap-1">
            <BookOpen size={14} />
            {resources.readings.length} readings
          </span>
        </div>
        <motion.div animate={{ rotate: isExpanded ? 90 : 0 }}>
          <CaretRight size={18} className="text-zinc-500" />
        </motion.div>
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-5 space-y-5">
              {/* Videos */}
              <div>
                <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <MonitorPlay size={14} className="text-orange-400" />
                  Watch & Learn
                </h4>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {resources.videos.map((video, i) => (
                    <VideoCard key={i} video={video} onPlay={onPlayVideo} />
                  ))}
                </div>
              </div>

              {/* Readings */}
              <div>
                <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <BookOpen size={14} className="text-orange-400" />
                  Read & Explore
                </h4>
                <div className="space-y-2">
                  {resources.readings.map((reading, i) => (
                    <ReadingCard key={i} reading={reading} />
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Main Resources Tab Component
const ResourcesTab = () => {
  const [expandedStage, setExpandedStage] = useState(1);
  const [playingVideo, setPlayingVideo] = useState(null);

  const totalVideos = Object.values(STAGE_RESOURCES).reduce((sum, s) => sum + s.videos.length, 0);
  const totalReadings = Object.values(STAGE_RESOURCES).reduce((sum, s) => sum + s.readings.length, 0);

  return (
    <div data-testid="resources-tab">
      {/* Header */}
      <div className="mb-6">
        <span className="text-xs text-orange-500 uppercase tracking-widest font-semibold">Curated Learning Library</span>
        <h2 className="text-2xl font-outfit font-bold text-white mt-1">Videos & Readings</h2>
        <p className="text-sm text-zinc-400 mt-2">
          Hand-picked from MIT, Khan Academy, 3Blue1Brown, freeCodeCamp, and top finance educators.
        </p>
        <div className="flex gap-4 mt-3">
          <span className="text-xs text-zinc-500 flex items-center gap-1.5">
            <MonitorPlay size={14} className="text-orange-400" />
            {totalVideos} videos
          </span>
          <span className="text-xs text-zinc-500 flex items-center gap-1.5">
            <BookOpen size={14} className="text-orange-400" />
            {totalReadings} readings
          </span>
          <span className="text-xs text-zinc-500 flex items-center gap-1.5">
            <Clock size={14} className="text-orange-400" />
            All free
          </span>
        </div>
      </div>

      {/* Stage Sections */}
      <div className="space-y-3">
        {Object.entries(STAGE_RESOURCES).map(([stageId, resources]) => (
          <StageSection
            key={stageId}
            stageId={parseInt(stageId)}
            resources={resources}
            isExpanded={expandedStage === parseInt(stageId)}
            onToggle={() => setExpandedStage(expandedStage === parseInt(stageId) ? null : parseInt(stageId))}
            onPlayVideo={setPlayingVideo}
          />
        ))}
      </div>

      {/* Video Player Modal */}
      <AnimatePresence>
        {playingVideo && (
          <VideoPlayer
            videoId={playingVideo.id}
            title={playingVideo.title}
            onClose={() => setPlayingVideo(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ResourcesTab;
